import {
  MdOutlineZoomIn,
  MdOutlineZoomOut,
  MdDoneAll,
  MdClose,
} from "react-icons/md";
import Slider from "./Slider";
import ZoomButton from "./ZoomButton";
import { useCallback, useEffect, useState } from "react";
import { animate } from "framer-motion";

interface ImageScalingToolProps {
  defaultScale?: number;
  imageScale?: number;
  setImageScale?: React.Dispatch<React.SetStateAction<number>>;
  onApproveClick?: () => void;
  onCancelClick?: () => void;
  minScale?: number;
  maxScale?: number;
  stepScale?: number;
}

const ImageScalingTool: React.FC<ImageScalingToolProps> = ({
  defaultScale = 1,
  imageScale,
  setImageScale,
  onApproveClick,
  onCancelClick,
  minScale = 0,
  maxScale = 2,
  stepScale = 0.01,
}) => {
  // States definition
  const [_imageScale, _setImageScale] = useState(0);

  /**
   * Wrapper function used to include an external definition
   * of the state (if defined)
   */
  const setImageScaleWrapper = useCallback(
    (value: number) => {
      // Set the internal image scale
      _setImageScale(value);
      // And set the external if defined
      if (setImageScale) setImageScale(value);
    },
    [_setImageScale, setImageScale],
  );

  /**
   * Sync when controlled value changes
   */
  useEffect(() => {
    if (imageScale !== undefined) {
      _setImageScale(imageScale);
    }
  }, [imageScale]);

  /**
   * First animation for the slider appearance
   */
  useEffect(() => {
    const controls = animate(0, defaultScale, {
      duration: 0.7,
      ease: "circOut",
      onUpdate: (latest) => _setImageScale(latest),
    });

    return () => controls.stop();
  }, []);

  return (
    <div className="flex flex-col p-3 gap-6 bg-neutral-500/60 rounded-xl overflow-hidden">
      <div className="flex h-fit justify-center items-start gap-3">
        <div className="flex flex-col items-start gap-2">
          <p className="overflow-hidden text-sm font-sans text-white font-semibold overflow-ellipsis text-nowrap select-none">
            Default zoom
          </p>
          <ZoomButton
            upperText="Max"
            lowerText="200%"
            onClick={() => setImageScaleWrapper(2)}
            selected={_imageScale === 2}
          />
          <ZoomButton
            upperText="Large"
            lowerText="150%"
            onClick={() => setImageScaleWrapper(1.5)}
            selected={_imageScale === 1.5}
          />
          <ZoomButton
            upperText="Default"
            lowerText="100%"
            onClick={() => setImageScaleWrapper(1)}
            selected={_imageScale === 1}
          />
          <ZoomButton
            upperText="Small"
            lowerText="75%"
            onClick={() => setImageScaleWrapper(0.75)}
            selected={_imageScale === 0.75}
          />
          <ZoomButton
            upperText="Tiny"
            lowerText="50%"
            onClick={() => setImageScaleWrapper(0.5)}
            selected={_imageScale === 0.5}
          />
        </div>
        <div className="flex flex-col gap-2 self-stretch">
          <MdOutlineZoomIn className="size-5 fill-white shrink-0" />
          <Slider
            value={_imageScale}
            setValue={setImageScaleWrapper}
            min={minScale}
            max={maxScale}
            step={stepScale}
            accentColor="var(--color-secondary-500)"
          />
          <MdOutlineZoomOut className="size-5 fill-white shrink-0" />
        </div>
      </div>
      {/* Approve buttons */}
      <div className="flex flex-col gap-3">
        <button
          onClick={onApproveClick}
          className="flex p-2 justify-center items-center gap-2 self-stretch bg-neutral-100/70 hover:bg-neutral-100 ease-in-out duration-150 rounded-full cursor-pointer"
        >
          <MdDoneAll className="size-5 shrink-0 fill-secondary-600" />
          <p className="text-secondary-600 text-sm font-sans font-bold leading-4">
            Approve
          </p>
        </button>
        <button
          onClick={onCancelClick}
          className="flex p-2 justify-center items-center gap-2 self-stretch bg-neutral-100/70 hover:bg-neutral-100 ease-in-out duration-150 rounded-full cursor-pointer"
        >
          <MdClose className="size-5 shrink-0 fill-neutral-700" />
          <p className="text-neutral-700 text-sm font-sans font-bold leading-4">
            Cancel
          </p>
        </button>
      </div>
    </div>
  );
};

export default ImageScalingTool;
